import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from './ui/carousel';
import { Icon } from './Icon';
import { useScrubber } from '../hooks/useScrubber';
import { haptic } from '../lib/haptic';
import { playSfx } from '../lib/sfx';
import './CardCarousel.css';
import { ICON_SIZE } from '../styles/tokens';

type Props<T> = {
  items: T[];
  getKey: (item: T, index: number) => string;
  renderCard: (item: T, isActive: boolean, index: number) => ReactNode;
  currentIdx: number;
  onIndexChange: (index: number) => void;
  onPrev?: () => void;
  onNext?: () => void;
  prevLabel?: string;
  nextLabel?: string;
  /** Cards farther than this from the active index render as lightweight ghosts. Default: no windowing. */
  windowSize?: number;
  /** Slot width as a percentage of the track. Default: 84. */
  slotWidthPercent?: number;
  /** Minimum thumb percentage for the scrubber bar. Default: 8. */
  minThumbPercent?: number;
  /** Algo decorativo junto a las flechas (la mascota de la sección, `<SectionMascot>`):
   *  a su izquierda en la fila, debajo de ellas en la columna acostada. */
  aside?: ReactNode;
};

/**
 * CardCarousel: Built strictly using Neobrutalism Carousel (https://www.neobrutalism.dev/docs/carousel)
 * powered by embla-carousel-react. Replaces custom scroll-snap tracks across
 * DetailView (Tarjetas), LetrasView, and SilabasView.
 */
export function CardCarousel<T>({
  items,
  getKey,
  renderCard,
  currentIdx,
  onIndexChange,
  onPrev,
  onNext,
  prevLabel = 'Anterior',
  nextLabel = 'Siguiente',
  windowSize = Infinity,
  slotWidthPercent = 84,
  minThumbPercent = 8,
  aside,
}: Props<T>) {
  const [api, setApi] = useState<CarouselApi>();

  // Synchronize Embla when currentIdx changes from parent/scrubber
  useEffect(() => {
    if (!api) return;
    if (api.selectedScrollSnap() !== currentIdx) {
      api.scrollTo(currentIdx);
    }
  }, [api, currentIdx]);

  // Listen to Embla slide select event (touch swipe, drag, or key)
  useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      const selected = api.selectedScrollSnap();
      if (selected !== currentIdx) {
        onIndexChange(selected);
      }
    };

    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api, currentIdx, onIndexChange]);

  // Scrubber drag-to-seek
  const scrollToIndex = useCallback(
    (idx: number) => {
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      onIndexChange(clamped);
      api?.scrollTo(clamped);
    },
    [items.length, onIndexChange, api]
  );

  const scrubber = useScrubber(items.length, currentIdx, scrollToIndex, {
    minThumbPercent,
  });

  const handlePrev = useCallback(() => {
    haptic('light');
    playSfx('page');
    if (onPrev) {
      onPrev();
    } else {
      const target = Math.max(0, currentIdx - 1);
      onIndexChange(target);
      api?.scrollPrev();
    }
  }, [onPrev, api, currentIdx, onIndexChange]);

  const handleNext = useCallback(() => {
    haptic('light');
    playSfx('page');
    if (onNext) {
      onNext();
    } else {
      const target = Math.min(items.length - 1, currentIdx + 1);
      onIndexChange(target);
      api?.scrollNext();
    }
  }, [onNext, api, currentIdx, items.length, onIndexChange]);

  const slotStyle = {
    '--slot-width': `${slotWidthPercent}%`,
  } as CSSProperties;

  return (
    <Carousel
      setApi={setApi}
      opts={{
        align: 'center',
        containScroll: false,
        loop: false,
        skipSnaps: false,
      }}
      className="card-carousel"
    >
      {items.length > 1 && (
        <div
          className="card-carousel-scrollbar"
          ref={scrubber.scrollbarRef}
          onPointerDown={scrubber.onPointerDown}
          onPointerMove={scrubber.onPointerMove}
          onPointerUp={scrubber.onPointerUp}
          onPointerCancel={scrubber.onPointerCancel}
          role="slider"
          aria-label="Posición de tarjeta"
          aria-valuemin={1}
          aria-valuemax={items.length}
          aria-valuenow={currentIdx + 1}
        >
          <div className="card-carousel-scrollbar-thumb" style={scrubber.thumbStyle} />
        </div>
      )}

      <CarouselContent
        containerClassName="card-carousel-viewport"
        className="card-carousel-track"
      >
        {items.map((item, i) => {
          const isActive = i === currentIdx;
          const dist = Math.abs(i - currentIdx);
          return (
            <CarouselItem
              key={getKey(item, i)}
              className="card-carousel-slot"
              style={slotStyle}
            >
              {dist <= windowSize ? (
                renderCard(item, isActive, i)
              ) : (
                <div className="card-carousel-slot-ghost" aria-hidden="true" />
              )}
            </CarouselItem>
          );
        })}
      </CarouselContent>

      <div className="card-carousel-controls">
        <div className="card-carousel-buttons-row">
          <button
            type="button"
            className="icon-btn icon-btn--lg card-carousel-arrow"
            onClick={handlePrev}
            disabled={currentIdx === 0}
            aria-label={prevLabel}
          >
            <Icon name="arrow-left" size={ICON_SIZE.xl} />
          </button>

          <div className="card-carousel-info-row">
            <span className="card-carousel-page-indicator" aria-live="polite">
              {currentIdx + 1}{' '}
              <span className="card-carousel-page-of">de</span> {items.length}
            </span>
          </div>

          <button
            type="button"
            className="icon-btn icon-btn--lg card-carousel-arrow"
            onClick={handleNext}
            disabled={currentIdx === items.length - 1}
            aria-label={nextLabel}
          >
            <Icon name="arrow-right" size={ICON_SIZE.xl} />
          </button>
        </div>
        {aside && <div className="card-carousel-aside">{aside}</div>}
      </div>
    </Carousel>
  );
}
